/**
 * Project Page Preview Component
 *
 * Displays the project page as it would appear on fixmybrick.ca.
 * Matches the "Industrial Editorial" design from the marketing site:
 * - Light theme with stone/brick color palette
 * - Desktop sticky sidebar with project details
 * - Hero with gradient overlay
 *
 * @see /fixmybrick-marketing-site/src/app/(marketing)/projects/[slug]/page.tsx
 * @see /fixmybrick-marketing-site/src/components/projects/ProjectSummaryCard.tsx
 */

import React from 'react'
import {
  MapPin,
  Calendar,
  Clock,
  Star,
  Quote,
  Wrench,
  DollarSign,
  Layers,
  ArrowRight,
  Phone,
} from 'lucide-react'
import type { GalleryImage, ProjectPage } from '@/types/projectPages'

interface ProjectPagePreviewProps {
  projectPage: ProjectPage
  className?: string
}

const normalizeGalleryImages = (images?: ProjectPage['gallery_images']): GalleryImage[] => {
  if (!Array.isArray(images)) return []

  const rawImages = images as unknown[]

  return rawImages.map((entry, index) => {
    if (typeof entry === 'string') {
      return {
        url: entry,
        alt: `Gallery image ${index + 1}`,
        caption: ''
      }
    }

    const typedEntry = entry as GalleryImage
    return {
      url: typedEntry.url,
      alt: typedEntry.alt || `Gallery image ${index + 1}`,
      caption: typedEntry.caption || ''
    }
  })
}

/**
 * Formats a date string to "November 2024" format
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })
}

export default function ProjectPagePreview({ projectPage, className = '' }: ProjectPagePreviewProps) {
  const galleryImages = normalizeGalleryImages(projectPage.gallery_images)
  const formattedCompletionDate = projectPage.completion_date
    ? formatDate(projectPage.completion_date)
    : null

  const backgroundImage = projectPage.hero_image_url ||
    galleryImages[0]?.url ||
    'https://images.unsplash.com/photo-1635424710928-0544e8512eae?q=80&w=1740&auto=format&fit=crop'

  const location = projectPage.service_city && projectPage.service_province
    ? `${projectPage.service_city}, ${projectPage.service_province}`
    : projectPage.service_city || null

  const tags = projectPage.tags || []
  const materials = projectPage.materials_used || []

  return (
    <div className={`min-h-screen bg-stone-50 ${className}`}>
      {/* Hero Section - Matching marketing site */}
      <section className="relative bg-black text-white overflow-hidden">
        {/* Featured Image with overlay */}
        <div className="relative h-[300px] sm:h-[400px] md:h-[500px]">
          <img
            src={backgroundImage}
            alt={`${projectPage.title} featured image`}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>

        {/* Content overlay at bottom */}
        <div className="absolute inset-0 flex flex-col justify-end">
          <div className="container mx-auto px-4 pb-6 md:pb-8">
            <div className="max-w-4xl">
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                {projectPage.service_type && (
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm rounded">
                    {projectPage.service_type}
                  </span>
                )}
                {location && (
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm rounded">
                    {location}
                  </span>
                )}
                {projectPage.featured && (
                  <span className="px-3 py-1 bg-amber-400 text-stone-900 text-sm font-semibold rounded flex items-center gap-1">
                    <Star className="h-3.5 w-3.5" /> Featured
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white drop-shadow-lg">
                {projectPage.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area with Sticky Sidebar */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-8 space-y-6">
            {/* Project Gallery */}
            {galleryImages.length > 0 && (
              <section className="bg-white rounded border border-stone-200 p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-stone-900">Project Gallery</h2>
                  <span className="text-sm text-stone-500">
                    {galleryImages.length} {galleryImages.length === 1 ? 'photo' : 'photos'}
                  </span>
                </div>
                <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
                  {galleryImages.map((image, index) => (
                    <div key={index} className="group relative overflow-hidden rounded-lg aspect-[4/3]">
                      <img
                        src={image.url}
                        alt={image.alt || `${projectPage.title} - Image ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover transition duration-300 group-hover:scale-105"
                      />
                      {image.caption && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                          <p className="text-sm text-white">{image.caption}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Project Description */}
            <section className="bg-white rounded border border-stone-200 p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-stone-900 mb-4 md:mb-6">Project Details</h2>

              {projectPage.description ? (
                <div className="prose prose-stone prose-sm md:prose-base max-w-none">
                  {projectPage.description.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4 text-stone-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500 italic">Project description will appear here once added.</p>
              )}

              {/* Challenge & Solution */}
              {(projectPage.challenge_description || projectPage.solution_description) && (
                <div className="mt-6 pt-6 border-t border-stone-100 space-y-4">
                  {projectPage.challenge_description && (
                    <div className="bg-stone-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center">
                          <span className="text-amber-600 text-xs">!</span>
                        </span>
                        Challenge
                      </h3>
                      <p className="text-stone-700 text-sm leading-relaxed">{projectPage.challenge_description}</p>
                    </div>
                  )}
                  {projectPage.solution_description && (
                    <div className="bg-stone-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center">
                          <span className="text-emerald-600 text-xs">✓</span>
                        </span>
                        Solution
                      </h3>
                      <p className="text-stone-700 text-sm leading-relaxed">{projectPage.solution_description}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div className="mt-6 pt-6 border-t border-stone-100">
                  <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-sm bg-stone-100 text-stone-700 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Testimonial Section */}
            {projectPage.testimonial_text && (
              <section className="bg-white rounded border border-stone-200 p-6 md:p-8">
                <div className="relative">
                  <Quote className="absolute -top-1 -left-1 h-10 w-10 text-brick/10" />
                  <blockquote className="pl-8 text-lg md:text-xl text-stone-700 leading-relaxed italic">
                    "{projectPage.testimonial_text}"
                  </blockquote>
                  {projectPage.testimonial_author && (
                    <cite className="block mt-4 pl-8 text-base font-semibold text-stone-900 not-italic">
                      — {projectPage.testimonial_author}
                    </cite>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Right Column - Sticky Sidebar (Desktop) */}
          <div className="hidden lg:block lg:col-span-4">
            <aside className="bg-white border border-stone-200 rounded shadow-sm lg:sticky lg:top-24">
              {/* Header */}
              <div className="px-5 py-4 border-b border-stone-100">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
                  Project Details
                </h2>
              </div>

              {/* Details List */}
              <div className="divide-y divide-stone-100">
                {/* Service Type */}
                {projectPage.service_type && (
                  <div className="px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                      <Wrench className="h-4 w-4 text-stone-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-0.5">Service</p>
                      <p className="text-sm font-semibold text-stone-900">{projectPage.service_type}</p>
                    </div>
                  </div>
                )}

                {/* Location */}
                {location && (
                  <div className="px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-stone-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-0.5">Location</p>
                      <p className="text-sm font-semibold text-stone-900">{location}</p>
                    </div>
                  </div>
                )}

                {/* Price Range */}
                {projectPage.price_range && (
                  <div className="px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-stone-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-0.5">Investment</p>
                      <p className="text-sm font-semibold text-stone-900">{projectPage.price_range}</p>
                    </div>
                  </div>
                )}

                {/* Completion Date */}
                {formattedCompletionDate && (
                  <div className="px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-stone-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-0.5">Completed</p>
                      <p className="text-sm font-semibold text-stone-900">{formattedCompletionDate}</p>
                    </div>
                  </div>
                )}

                {/* Duration */}
                {projectPage.project_duration && (
                  <div className="px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-stone-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-0.5">Duration</p>
                      <p className="text-sm font-semibold text-stone-900">{projectPage.project_duration}</p>
                    </div>
                  </div>
                )}

                {/* Materials Used */}
                {materials.length > 0 && (
                  <div className="px-5 py-4 flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                      <Layers className="h-4 w-4 text-stone-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-2">Materials</p>
                      <div className="flex flex-wrap gap-1.5">
                        {materials.slice(0, 4).map((material) => (
                          <span
                            key={material}
                            className="inline-block px-2 py-0.5 text-xs bg-stone-100 text-stone-700 rounded"
                          >
                            {material}
                          </span>
                        ))}
                        {materials.length > 4 && (
                          <span className="inline-block px-2 py-0.5 text-xs bg-stone-100 text-stone-500 rounded">
                            +{materials.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Badge (Admin-only info) */}
                <div className="px-5 py-4 flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded bg-stone-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-stone-600">ST</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-stone-500 uppercase tracking-wide mb-0.5">Status</p>
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded capitalize ${
                      projectPage.status === 'published'
                        ? 'bg-emerald-100 text-emerald-700'
                        : projectPage.status === 'draft'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-stone-100 text-stone-700'
                    }`}>
                      {projectPage.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="p-5 bg-stone-50 border-t border-stone-100 space-y-3">
                <button className="flex items-center justify-center gap-2 w-full rounded bg-brick px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-brick-dark">
                  Get Free Estimate
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button className="flex items-center justify-center gap-2 w-full rounded border-2 border-stone-900 bg-white px-4 py-2.5 text-sm font-semibold text-stone-900 transition-all hover:bg-stone-900 hover:text-white">
                  <Phone className="h-4 w-4" />
                  (905) 807-0404
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Mobile Summary Card (shows on mobile/tablet) */}
      <div className="lg:hidden bg-white border-t border-stone-200 p-4">
        <div className="flex flex-wrap gap-4 text-sm text-stone-600 mb-4">
          {projectPage.service_type && (
            <div className="flex items-center gap-1.5">
              <Wrench className="h-4 w-4 text-stone-400" />
              {projectPage.service_type}
            </div>
          )}
          {location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-stone-400" />
              {location}
            </div>
          )}
          {formattedCompletionDate && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-stone-400" />
              {formattedCompletionDate}
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button className="flex-1 rounded bg-brick px-4 py-3 text-sm font-semibold text-white">
            Get Free Estimate
          </button>
          <button className="flex-1 rounded border-2 border-stone-900 px-4 py-2.5 text-sm font-semibold text-stone-900">
            Call Now
          </button>
        </div>
      </div>

      {/* Final CTA Section */}
      <section className="py-10 md:py-14 bg-stone-900 text-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <p className="text-sm uppercase tracking-[0.2em] text-stone-400 mb-3">
            Ready to Start?
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Want Similar Results?</h2>
          <p className="text-base md:text-lg text-stone-400 mb-8">
            Browse similar success stories or schedule a consultation to plan your own{' '}
            {projectPage.service_type?.toLowerCase() || 'masonry'} transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="inline-flex items-center justify-center gap-2 rounded bg-brick px-6 py-3 text-base font-semibold text-white transition-all hover:bg-brick-dark">
              Get Your Free Estimate
              <ArrowRight className="h-4 w-4" />
            </button>
            <button className="inline-flex items-center justify-center gap-2 rounded border-2 border-white/30 px-6 py-3 text-base font-semibold text-white transition-all hover:bg-white/10 hover:border-white/50">
              View Portfolio
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
